<template>
  <div class="mt-2 mb-4" data-testid="junifer-comm-content">
    <h5 class="mb-2">Email</h5>
    <SoAlert v-if="!email" type="info" data-testid="junifer-comm-not-sent">
      The customer didn't receive this communication. Junifer created it but
      sent no email or letter (No deliveries).
    </SoAlert>
    <SoSkeleton
      v-else-if="email.isFetching"
      height="200"
      data-testid="junifer-comm-email-loading"
    />
    <SoAlert
      v-else-if="hasNoStoredEmail"
      type="info"
      data-testid="junifer-comm-no-email"
    >
      <template v-if="comm.hasFailed">
        The customer didn't receive this communication. Junifer tried to send it
        but it failed, so there's no email or letter to show (Failed).
      </template>
      <template v-else>
        Junifer has no email stored for this communication.
      </template>
    </SoAlert>
    <SoAlert
      v-else-if="email.isError"
      type="error"
      data-testid="junifer-comm-email-error"
    >
      <div class="d-flex align-center justify-space-between ga-4">
        Couldn't load this email from Junifer.
        <SoButton
          title="Retry"
          data-test-id="junifer-comm-email-retry"
          @click="email.refetch()"
        />
      </div>
    </SoAlert>
    <SoAlert
      v-else-if="!email.data"
      type="info"
      data-testid="junifer-comm-letter-only"
    >
      No email was sent for this communication. It went as a letter: see the
      document below.
    </SoAlert>
    <JuniferCommEmailFrame
      v-else
      :subject="email.data.subject"
      :html="email.data.body"
    />

    <template v-if="comm.files.length">
      <h5 class="mt-4 mb-2">Documents</h5>
      <div data-testid="junifer-comm-documents">
        <JuniferCommDocumentButton
          v-for="file in comm.files"
          :key="file.id"
          :communication-id="comm.id"
          :file="file"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import JuniferCommEmailFrame from "./JuniferCommEmailFrame.vue"
  import JuniferCommDocumentButton from "./JuniferCommDocumentButton.vue"
  import { JuniferService } from "@/services/JuniferService"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { IJuniferComm } from "@/models/juniferCommunication/juniferComm.types"
  import { isBackofficeApiError } from "@/api/backofficeRest/backofficeApiError"

  // Mounted inside the timeline item's lazy expansion panel, so the email is
  // only fetched once the agent expands the communication.
  export default defineComponent({
    components: {
      SoAlert,
      SoButton,
      SoSkeleton,
      JuniferCommEmailFrame,
      JuniferCommDocumentButton,
    },
    props: {
      comm: {
        type: Object as PropType<IJuniferComm>,
        required: true,
      },
    },
    data() {
      return {
        // Nothing to fetch when Junifer delivered nothing.
        email: this.comm.hasNoDeliveries
          ? null
          : JuniferService.getCommunicationEmail(
              useCurrentAccountStore().coreId.data,
              this.comm.id,
            ),
      }
    },
    computed: {
      // Junifer answers 404 when it holds no email for the communication, e.g.
      // one it failed to send. Retrying can't change that, so it isn't an error.
      hasNoStoredEmail(): boolean {
        const error = this.email?.error
        return (
          isBackofficeApiError(error) &&
          error.errorCode === "communication_not_found"
        )
      },
    },
  })
</script>
